import { isAdmin } from '@avslack/shared';
import { useClosePoll, usePoll, useVotePoll } from '../data/polls';
import { useUsers } from '../data/users';
import { useSession } from '../lib/session';
import { Icon } from './Icon';

/**
 * SZAVAZAS-KARTYA az uzenetben (versenyaudit U4).
 *
 * Egy kattintas szavaz; egyvalasztosnal a masik opcio atteszi a szavazatot,
 * a sajat valasztas ujboli kattintasa visszavonja. Az eredmeny mindig
 * latszik, elo: a csik az aranyt, a szam a darabot mutatja. Nevtelen
 * szavazasnal a nevek senkinek nem latszanak.
 */
export function PollCard({ pollId }: { pollId: string }) {
  const { data: poll, isLoading, isError } = usePoll(pollId);
  const vote = useVotePoll(pollId);
  const close = useClosePoll(pollId);
  const { nameOf } = useUsers();
  const me = useSession().data?.user;

  if (isLoading) return <p className="text-sm text-ink-muted">Szavazás betöltése…</p>;
  if (isError || !poll) return <p className="text-sm text-ink-muted">A szavazás nem érhető el.</p>;

  const mine = new Set(poll.myVotes);
  const total = poll.options.reduce((sum, o) => sum + o.votes, 0);
  const canClose = !poll.isClosed && me && (poll.createdBy === me.id || isAdmin(me));

  const toggle = (optionId: string) => {
    if (poll.isClosed || vote.isPending) return;
    const next = poll.multiple
      ? mine.has(optionId)
        ? [...mine].filter((id) => id !== optionId)
        : [...mine, optionId]
      : mine.has(optionId)
        ? []
        : [optionId];
    vote.mutate(next);
  };

  const deadline = poll.closesAt ? new Date(poll.closesAt) : null;

  return (
    <div className="mt-1 max-w-lg rounded-card border border-border-soft bg-surface-raised p-3">
      <p className="flex items-start gap-2 text-sm font-semibold">
        <span aria-hidden className="text-accent">
          📊
        </span>
        <span className="min-w-0 break-words">{poll.question}</span>
      </p>
      <p className="mt-0.5 text-2xs text-ink-faint">
        {poll.multiple ? 'Több is választható' : 'Egy választható'}
        {poll.anonymous ? ' · névtelen' : ''}
        {poll.isClosed
          ? ' · lezárult'
          : deadline
            ? ` · határidő: ${deadline.toLocaleString('hu-HU', { month: 'short', day: 'numeric', weekday: 'short', hour: '2-digit', minute: '2-digit' })}`
            : ''}
      </p>

      <ul className="mt-2 list-none space-y-1.5 pl-0" role={poll.multiple ? 'group' : 'radiogroup'}>
        {poll.options.map((option) => {
          const chosen = mine.has(option.id);
          const share = total > 0 ? Math.round((option.votes / total) * 100) : 0;
          const voters = option.voterIds.map((id) => nameOf(id)).join(', ');
          return (
            <li key={option.id} className="m-0 p-0">
              <button
                type="button"
                role={poll.multiple ? 'checkbox' : 'radio'}
                aria-checked={chosen}
                disabled={poll.isClosed || vote.isPending}
                onClick={() => toggle(option.id)}
                title={voters || undefined}
                className={[
                  'relative w-full overflow-hidden rounded-control border px-3 py-1.5 text-left text-sm transition-colors',
                  chosen ? 'border-accent' : 'border-border-soft hover:border-border-strong',
                  poll.isClosed ? 'cursor-default' : '',
                ].join(' ')}
              >
                {/* Az arany-csik a gomb hattereben. */}
                <span
                  aria-hidden
                  className={`absolute inset-y-0 left-0 ${chosen ? 'bg-accent-soft' : 'bg-surface-sunken'}`}
                  style={{ width: `${share}%` }}
                />
                <span className="relative flex items-center gap-2">
                  {chosen && <Icon name="pipa" size="sm" />}
                  <span className="min-w-0 flex-1 break-words">{option.text}</span>
                  <span className="shrink-0 text-xs tabular-nums text-ink-muted">
                    {option.votes} · {share}%
                  </span>
                </span>
              </button>
              {!poll.anonymous && voters && (
                <p className="mt-0.5 truncate pl-3 text-2xs text-ink-faint">{voters}</p>
              )}
            </li>
          );
        })}
      </ul>

      <div className="mt-2 flex items-center gap-2 text-2xs text-ink-faint">
        <span>
          {poll.voterCount} szavazó
          {!poll.isClosed && mine.size > 0 ? ' · a választásod módosítható' : ''}
        </span>
        {canClose && (
          <button
            type="button"
            onClick={() => close.mutate()}
            disabled={close.isPending}
            className="ml-auto rounded-control px-2 py-0.5 text-ink-muted hover:bg-surface-hover hover:text-ink"
          >
            Lezárás
          </button>
        )}
      </div>
      {(vote.isError || close.isError) && (
        <p role="alert" className="mt-1 text-2xs text-danger">
          {(vote.error ?? close.error) instanceof Error
            ? (vote.error ?? close.error)!.message
            : 'Nem sikerült.'}
        </p>
      )}
    </div>
  );
}
